import React, { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { 
  Users, Search, Filter, BarChart, Plus,
  FileText, Calendar, Clock, CheckCircle,
  Download, MessageSquare, ChevronRight,
  Briefcase, Award, Heart, Book, Coffee
} from 'lucide-react';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

const translations = {
  es: {
    title: 'Portal de Recursos Humanos',
    subtitle: 'Gestiona tus beneficios y documentos de empleado',
    sections: {
      overview: 'Vista General',
      benefits: 'Beneficios',
      documents: 'Documentos',
      requests: 'Solicitudes',
      training: 'Formación'
    },
    actions: {
      newRequest: 'Nueva solicitud',
      downloadDoc: 'Descargar',
      viewDetails: 'Ver detalles'
    },
    search: 'Buscar...',
    filters: 'Filtros',
    status: {
      active: 'Activo',
      pending: 'Pendiente',
      approved: 'Aprobado',
      rejected: 'Rechazado'
    }
  },
  en: {
    title: 'HR Portal',
    subtitle: 'Manage your employee benefits and documents',
    sections: {
      overview: 'Overview',
      benefits: 'Benefits',
      documents: 'Documents',
      requests: 'Requests',
      training: 'Training'
    },
    actions: {
      newRequest: 'New Request',
      downloadDoc: 'Download',
      viewDetails: 'View Details'
    },
    search: 'Search...',
    filters: 'Filters',
    status: {
      active: 'Active',
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected'
    }
  }
};

// Mock data
const mockBenefits = [
  {
    id: 1,
    name: 'Health Insurance',
    provider: 'GlobalHealth Plus',
    status: 'active',
    coverage: 'Full coverage',
    nextRenewal: '2025-12-31'
  },
  {
    id: 2,
    name: 'Dental Plan',
    provider: 'DentalCare Pro',
    status: 'active',
    coverage: 'Basic coverage',
    nextRenewal: '2025-12-31'
  },
  {
    id: 3,
    name: 'Life Insurance',
    provider: 'LifeSecure',
    status: 'active',
    coverage: '$100,000',
    nextRenewal: '2025-12-31'
  }
];

const mockDocuments = [
  {
    id: 1,
    name: 'Employee Handbook',
    type: 'PDF',
    size: '2.4 MB',
    lastUpdated: '2025-03-01'
  },
  {
    id: 2,
    name: 'Benefits Guide',
    type: 'PDF',
    size: '1.8 MB',
    lastUpdated: '2025-02-15'
  },
  {
    id: 3,
    name: 'Company Policies',
    type: 'PDF',
    size: '3.2 MB',
    lastUpdated: '2025-01-30'
  }
];

const mockRequests = [
  {
    id: 1,
    type: 'Vacation',
    startDate: '2025-04-01',
    endDate: '2025-04-05',
    status: 'pending',
    submittedDate: '2025-03-15'
  },
  {
    id: 2,
    type: 'Work from Home',
    startDate: '2025-03-20',
    endDate: '2025-03-20',
    status: 'approved',
    submittedDate: '2025-03-13'
  }
];

const mockTraining = [
  {
    id: 1,
    title: 'Cybersecurity Basics',
    progress: 75,
    dueDate: '2025-03-31',
    mandatory: true
  },
  {
    id: 2,
    title: 'Leadership Skills',
    progress: 30,
    dueDate: '2025-04-15',
    mandatory: false
  }
];

const HRPortalPage = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const { language } = useLanguage();
  const t = translations[language];
  const dateLocale = language === 'es' ? es : enUS;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t.title}</h1>
            <p className="text-foreground/70">{t.subtitle}</p>
          </div>
          <div className="flex space-x-4">
            <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90">
              <Plus className="h-4 w-4 mr-2" />
              {t.actions.newRequest}
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-border mb-8">
          <nav className="-mb-px flex space-x-8">
            {Object.entries(t.sections).map(([key, value]) => (
              <button
                key={key}
                onClick={() => setActiveSection(key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeSection === key
                    ? 'border-accent text-accent'
                    : 'border-transparent text-foreground/70 hover:text-foreground hover:border-border'
                }`}
              >
                {value}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Section */}
        {activeSection === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="dark-card p-6">
              <div className="flex items-center">
                <Heart className="h-8 w-8 text-red-500" />
                <div className="ml-4">
                  <p className="text-sm text-foreground/70">Health Benefits</p>
                  <p className="text-xl font-bold text-foreground">Active</p>
                </div>
              </div>
            </div>
            <div className="dark-card p-6">
              <div className="flex items-center">
                <Calendar className="h-8 w-8 text-blue-500" />
                <div className="ml-4">
                  <p className="text-sm text-foreground/70">Vacation Days</p>
                  <p className="text-xl font-bold text-foreground">15 days</p>
                </div>
              </div>
            </div>
            <div className="dark-card p-6">
              <div className="flex items-center">
                <Book className="h-8 w-8 text-purple-500" />
                <div className="ml-4">
                  <p className="text-sm text-foreground/70">Training</p>
                  <p className="text-xl font-bold text-foreground">2 pending</p>
                </div>
              </div>
            </div>
            <div className="dark-card p-6">
              <div className="flex items-center">
                <Coffee className="h-8 w-8 text-yellow-500" />
                <div className="ml-4">
                  <p className="text-sm text-foreground/70">Next Review</p>
                  <p className="text-xl font-bold text-foreground">30 days</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Benefits Section */}
        {activeSection === 'benefits' && (
          <div className="space-y-6">
            {mockBenefits.map((benefit) => (
              <div
                key={benefit.id}
                className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Heart className="h-5 w-5 text-accent" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">{benefit.name}</h3>
                      <p className="text-foreground/70">{benefit.provider}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    benefit.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {benefit.status.toUpperCase()}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-foreground/70">Coverage</p>
                    <p className="text-sm font-medium text-foreground">{benefit.coverage}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground/70">Next Renewal</p>
                    <p className="text-sm font-medium text-foreground">
                      {format(new Date(benefit.nextRenewal), 'PP', { locale: dateLocale })}
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  <button className="inline-flex items-center text-accent hover:text-accent/90">
                    {t.actions.viewDetails}
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Documents Section */}
        {activeSection === 'documents' && (
          <div className="space-y-6">
            {mockDocuments.map((document) => (
              <div
                key={document.id}
                className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-accent" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">{document.name}</h3>
                      <p className="text-foreground/70">
                        {document.type} · {document.size}
                      </p>
                    </div>
                  </div>
                  <button className="inline-flex items-center px-3 py-1 border border-border rounded-lg text-sm text-foreground hover:bg-secondary/30">
                    <Download className="h-4 w-4 mr-2" />
                    {t.actions.downloadDoc}
                  </button>
                </div>

                <div className="mt-4">
                  <p className="text-sm text-foreground/70">
                    Last updated: {format(new Date(document.lastUpdated), 'PP', { locale: dateLocale })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Requests Section */}
        {activeSection === 'requests' && (
          <div className="space-y-6">
            {mockRequests.map((request) => (
              <div
                key={request.id}
                className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-accent" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">{request.type}</h3>
                      <p className="text-foreground/70">
                        {format(new Date(request.startDate), 'PP', { locale: dateLocale })} - 
                        {format(new Date(request.endDate), 'PP', { locale: dateLocale })}
                      </p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    request.status === 'approved'
                      ? 'bg-green-100 text-green-800'
                      : request.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {t.status[request.status]}
                  </span>
                </div>

                <div className="mt-4">
                  <p className="text-sm text-foreground/70">
                    Submitted: {format(new Date(request.submittedDate), 'PP', { locale: dateLocale })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Training Section */}
        {activeSection === 'training' && (
          <div className="space-y-6">
            {mockTraining.map((course) => (
              <div
                key={course.id}
                className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Book className="h-5 w-5 text-accent" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">{course.title}</h3>
                      <div className="flex items-center mt-1">
                        {course.mandatory && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 mr-2">
                            Mandatory
                          </span>
                        )}
                        <span className="text-sm text-foreground/70">
                          Due: {format(new Date(course.dueDate), 'PP', { locale: dateLocale })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-foreground/70">Progress</span>
                    <span className="text-sm font-medium text-foreground">{course.progress}%</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full">
                    <div
                      className="h-2 bg-accent rounded-full"
                      style={{ width: `${course.progress}%` }}
                    ></div>
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  <button className="inline-flex items-center text-accent hover:text-accent/90">
                    Continue Training
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HRPortalPage;